import {
  AssessmentAnswer,
  AssessmentQuestion,
  ActivityListDataFragment,
  ActivityType,
  AssessmentSchemaType,
  AssessmentType,
  AssessmentQuestionType,
  ProgramActivityDataFragment,
  ConditionalMultipleChoiceAssessmentQuestion,
  FreeTextAssessmentAnswer,
  FreeTextAssessmentQuestion,
  MultiSelectAssessmentAnswer,
  MultiSelectAssessmentQuestion,
  MultiSignatureAssessmentAnswer,
  MultiSignatureAssessmentQuestion,
  MultipleChoiceAssessmentAnswer,
  MultipleChoiceAssessmentQuestion,
  MultipleChoiceScoredAssessmentAnswer,
  MultipleChoiceScoredAssessmentQuestion,
  RichTextAssessmentAnswer,
  RichTextAssessmentQuestion,
  SignatureAssessmentAnswer,
  SignatureAssessmentQuestion,
  StatementAssessmentQuestion,
  InitialsAssessmentQuestion,
  InitialsAssessmentAnswer,
  FileUploadAssessmentQuestion,
  FileUploadAssessmentAnswer,
  AutofillAssessmentQuestion,
  AutofillAssessmentAnswer,
  DatePickerAssessmentQuestion,
  DatePickerAssessmentAnswer,
  ProviderSignatureAssessmentQuestion,
  ProviderSignatureAssessmentAnswer,
  PatientContactAssessmentQuestion,
  PatientContactAssessmentAnswer,
} from '../../generated/graphql';
import { ProviderActivityType } from '../types/activity';

/**
 * Check if an assessment is marked as provider-only (cannot be sent via follow-ups)
 */
export const isProviderOnlyAssessment = (
  assessment: { completableByProviderOnly?: boolean | null } | null | undefined,
): boolean => {
  return assessment?.completableByProviderOnly === true;
};

export const isAssessmentAForm = (
  assessment: { schemaType?: AssessmentSchemaType | null } | null | undefined,
) => Boolean(assessment?.schemaType === AssessmentSchemaType.Custom);

export const isStandardAssessment = (
  providerActivity?: ActivityListDataFragment,
): boolean => {
  if (!providerActivity) {
    return false;
  }

  return (
    providerActivity.__typename === ProviderActivityType.Assessment &&
    !isAssessmentAForm(providerActivity.assessment)
  );
};

export enum AssessmentQuestionTypename {
  MultipleChoiceScoredAssessmentQuestion = 'MultipleChoiceScoredAssessmentQuestion',
  MultipleChoiceAssessmentQuestion = 'MultipleChoiceAssessmentQuestion',
  FreeTextAssessmentQuestion = 'FreeTextAssessmentQuestion',
  RichTextAssessmentQuestion = 'RichTextAssessmentQuestion',
  MultipleChoiceScoredAssessmentQuestionAnswerOption = 'MultipleChoiceScoredAssessmentQuestionAnswerOption',
}

const ASSESSMENT_QUESTION_TYPENAME_TO_TYPE: Partial<
  Record<string, AssessmentQuestionType>
> = {
  MultipleChoiceScoredAssessmentQuestion:
    AssessmentQuestionType.MultipleChoiceScored,
  MultipleChoiceAssessmentQuestion: AssessmentQuestionType.MultipleChoice,
  MultiSelectAssessmentQuestion: AssessmentQuestionType.MultiSelect,
  ConditionalMultipleChoiceAssessmentQuestion:
    AssessmentQuestionType.ConditionalMultipleChoice,
  FreeTextAssessmentQuestion: AssessmentQuestionType.FreeText,
  RichTextAssessmentQuestion: AssessmentQuestionType.RichText,
  StatementAssessmentQuestion: AssessmentQuestionType.Statement,
  InitialsAssessmentQuestion: AssessmentQuestionType.Initials,
  SignatureAssessmentQuestion: AssessmentQuestionType.Signature,
  MultiSignatureAssessmentQuestion: AssessmentQuestionType.MultiSignature,
  ProviderSignatureAssessmentQuestion: AssessmentQuestionType.ProviderSignature,
  FileUploadAssessmentQuestion: AssessmentQuestionType.FileUpload,
  AutofillAssessmentQuestion: AssessmentQuestionType.Autofill,
  DatePickerAssessmentQuestion: AssessmentQuestionType.DatePicker,
  PatientContactAssessmentQuestion: AssessmentQuestionType.PatientContact,
  UnsupportedAssessmentQuestion: AssessmentQuestionType.Unsupported,
};

type AssessmentQuestionTypenameSource = {
  __typename?: string;
};

export function getAssessmentQuestionType(
  assessmentQuestion: AssessmentQuestion,
): AssessmentQuestionType | undefined {
  if (assessmentQuestion.questionType != null) {
    return assessmentQuestion.questionType;
  }

  const typename = (assessmentQuestion as AssessmentQuestionTypenameSource)
    .__typename;
  if (!typename) {
    return undefined;
  }

  return ASSESSMENT_QUESTION_TYPENAME_TO_TYPE[typename];
}

// When using these, we assume that *all* the subfields are requested, otherwise typing will be off

export function isMultipleChoiceScoredAssessmentQuestion(
  assessmentQuestion: AssessmentQuestion,
): assessmentQuestion is MultipleChoiceScoredAssessmentQuestion {
  return (
    getAssessmentQuestionType(assessmentQuestion) ===
    AssessmentQuestionType.MultipleChoiceScored
  );
}

export function isMultipleChoiceAssessmentQuestion(
  assessmentQuestion: AssessmentQuestion,
): assessmentQuestion is MultipleChoiceAssessmentQuestion {
  return (
    getAssessmentQuestionType(assessmentQuestion) ===
    AssessmentQuestionType.MultipleChoice
  );
}

export function isConditionalMultipleChoiceAssessmentQuestion(
  assessmentQuestion: AssessmentQuestion,
): assessmentQuestion is ConditionalMultipleChoiceAssessmentQuestion {
  return (
    getAssessmentQuestionType(assessmentQuestion) ===
    AssessmentQuestionType.ConditionalMultipleChoice
  );
}

export function isFreeTextAssessmentQuestion(
  assessmentQuestion: AssessmentQuestion,
): assessmentQuestion is FreeTextAssessmentQuestion {
  return (
    getAssessmentQuestionType(assessmentQuestion) ===
    AssessmentQuestionType.FreeText
  );
}

export function isRichTextAssessmentQuestion(
  assessmentQuestion: AssessmentQuestion,
): assessmentQuestion is RichTextAssessmentQuestion {
  return (
    getAssessmentQuestionType(assessmentQuestion) ===
    AssessmentQuestionType.RichText
  );
}

export function isStatementAssessmentQuestion(
  assessmentQuestion: AssessmentQuestion,
): assessmentQuestion is StatementAssessmentQuestion {
  return (
    getAssessmentQuestionType(assessmentQuestion) ===
    AssessmentQuestionType.Statement
  );
}

export function isMultiSelectAssessmentQuestion(
  assessmentQuestion: AssessmentQuestion,
): assessmentQuestion is MultiSelectAssessmentQuestion {
  return (
    getAssessmentQuestionType(assessmentQuestion) ===
    AssessmentQuestionType.MultiSelect
  );
}

export function isInitialsAssessmentQuestion(
  assessmentQuestion: AssessmentQuestion,
): assessmentQuestion is InitialsAssessmentQuestion {
  return (
    getAssessmentQuestionType(assessmentQuestion) ===
    AssessmentQuestionType.Initials
  );
}

export function isSignatureAssessmentQuestion(
  assessmentQuestion: AssessmentQuestion,
): assessmentQuestion is SignatureAssessmentQuestion {
  return (
    getAssessmentQuestionType(assessmentQuestion) ===
    AssessmentQuestionType.Signature
  );
}

export function isMultiSignatureAssessmentQuestion(
  assessmentQuestion: AssessmentQuestion,
): assessmentQuestion is MultiSignatureAssessmentQuestion {
  return (
    getAssessmentQuestionType(assessmentQuestion) ===
    AssessmentQuestionType.MultiSignature
  );
}

export function isFileUploadAssessmentQuestion(
  assessmentQuestion: AssessmentQuestion,
): assessmentQuestion is FileUploadAssessmentQuestion {
  return (
    getAssessmentQuestionType(assessmentQuestion) ===
    AssessmentQuestionType.FileUpload
  );
}

export function isAutofillAssessmentQuestion(
  assessmentQuestion: AssessmentQuestion,
): assessmentQuestion is AutofillAssessmentQuestion {
  return (
    getAssessmentQuestionType(assessmentQuestion) ===
    AssessmentQuestionType.Autofill
  );
}

export function isDatePickerAssessmentQuestion(
  assessmentQuestion: AssessmentQuestion,
): assessmentQuestion is DatePickerAssessmentQuestion {
  return (
    getAssessmentQuestionType(assessmentQuestion) ===
    AssessmentQuestionType.DatePicker
  );
}

export function isPatientContactAssessmentQuestion(
  assessmentQuestion: AssessmentQuestion,
): assessmentQuestion is PatientContactAssessmentQuestion {
  return (
    getAssessmentQuestionType(assessmentQuestion) ===
    AssessmentQuestionType.PatientContact
  );
}

export function isProviderSignatureAssessmentQuestion(
  assessmentQuestion: AssessmentQuestion,
): assessmentQuestion is ProviderSignatureAssessmentQuestion {
  return (
    getAssessmentQuestionType(assessmentQuestion) ===
    AssessmentQuestionType.ProviderSignature
  );
}

export const isSignatureTypeAssessmentQuestion = (
  question: AssessmentQuestion,
) => {
  const isInitialsQuestion = isInitialsAssessmentQuestion(question);
  const isSignatureQuestion = isSignatureAssessmentQuestion(question);
  const isMultiSignatureQuestion = isMultiSignatureAssessmentQuestion(question);
  const isProviderSignatureQuestion =
    isProviderSignatureAssessmentQuestion(question);

  return (
    isInitialsQuestion ||
    isSignatureQuestion ||
    isMultiSignatureQuestion ||
    isProviderSignatureQuestion
  );
};

export const isClientSignatureTypeAssessmentQuestion = (
  question: AssessmentQuestion,
) => {
  const isInitialsQuestion = isInitialsAssessmentQuestion(question);
  const isSignatureQuestion = isSignatureAssessmentQuestion(question);

  return isInitialsQuestion || isSignatureQuestion;
};

// AssessmentAnswer type check functions

export function isFreeTextAssessmentAnswer(
  assessmentAnswer: AssessmentAnswer,
): assessmentAnswer is FreeTextAssessmentAnswer {
  return assessmentAnswer.questionType === AssessmentQuestionType.FreeText;
}

export function isMultipleChoiceScoredAssessmentAnswer(
  assessmentAnswer: AssessmentAnswer,
): assessmentAnswer is MultipleChoiceScoredAssessmentAnswer {
  return (
    assessmentAnswer.questionType ===
    AssessmentQuestionType.MultipleChoiceScored
  );
}

export function isMultipleChoiceAssessmentAnswer(
  assessmentAnswer: AssessmentAnswer,
): assessmentAnswer is MultipleChoiceAssessmentAnswer {
  return (
    assessmentAnswer.questionType === AssessmentQuestionType.MultipleChoice
  );
}

export function isMultiSelectAssessmentAnswer(
  assessmentAnswer: AssessmentAnswer,
): assessmentAnswer is MultiSelectAssessmentAnswer {
  return assessmentAnswer.questionType === AssessmentQuestionType.MultiSelect;
}

export function isRichTextAssessmentAnswer(
  assessmentAnswer: AssessmentAnswer,
): assessmentAnswer is RichTextAssessmentAnswer {
  return assessmentAnswer.questionType === AssessmentQuestionType.RichText;
}

export function isInitialsAssessmentAnswer(
  assessmentAnswer: AssessmentAnswer,
): assessmentAnswer is InitialsAssessmentAnswer {
  return assessmentAnswer.questionType === AssessmentQuestionType.Initials;
}

export function isSignatureAssessmentAnswer(
  assessmentAnswer: AssessmentAnswer,
): assessmentAnswer is SignatureAssessmentAnswer {
  return assessmentAnswer.questionType === AssessmentQuestionType.Signature;
}

export function isProviderSignatureAssessmentAnswer(
  assessmentAnswer: AssessmentAnswer,
): assessmentAnswer is ProviderSignatureAssessmentAnswer {
  return (
    assessmentAnswer.questionType === AssessmentQuestionType.ProviderSignature
  );
}

export function isMultiSignatureAssessmentAnswer(
  assessmentAnswer: AssessmentAnswer,
): assessmentAnswer is MultiSignatureAssessmentAnswer {
  return (
    assessmentAnswer.questionType === AssessmentQuestionType.MultiSignature
  );
}

export function isFileUploadAssessmentAnswer(
  assessmentAnswer: AssessmentAnswer,
): assessmentAnswer is FileUploadAssessmentAnswer {
  return assessmentAnswer.questionType === AssessmentQuestionType.FileUpload;
}

export function isAutofillAssessmentAnswer(
  assessmentAnswer: AssessmentAnswer,
): assessmentAnswer is AutofillAssessmentAnswer {
  return assessmentAnswer.questionType === AssessmentQuestionType.Autofill;
}

export function isDatePickerAssessmentAnswer(
  assessmentAnswer: AssessmentAnswer,
): assessmentAnswer is DatePickerAssessmentAnswer {
  return assessmentAnswer.questionType === AssessmentQuestionType.DatePicker;
}

export function isPatientContactAssessmentAnswer(
  assessmentAnswer: AssessmentAnswer,
): assessmentAnswer is PatientContactAssessmentAnswer {
  return (
    assessmentAnswer.questionType === AssessmentQuestionType.PatientContact
  );
}

export enum AssessmentCompletionDataTypename {
  MultipleChoiceSumScoredAssessmentCompletionData = 'MultipleChoiceSumScoredAssessmentCompletionData',
  HADSAssessmentCompletionData = 'HADSAssessmentCompletionData',
  ASRS19AssessmentCompletionData = 'ASRS19AssessmentCompletionData',
  CBIAssessmentCompletionData = 'CBIAssessmentCompletionData',
  CustomAssessmentCompletionData = 'CustomAssessmentCompletionData',
}

export function formatCompletionCount(
  count?: number | null,
): string | undefined {
  if (count == null || count < 1) {
    return undefined;
  }
  return count === 1 ? '1 completion' : `${count} completions`;
}

export type AssessmentHistorySelection = {
  id: string;
  type?: AssessmentType | null;
  schemaType?: AssessmentSchemaType | null;
  shortName?: string | null;
  name?: string | null;
};

export function shouldOpenPatientAssessmentHistory(
  programActivity: ProgramActivityDataFragment | undefined,
  trendsEnabled?: boolean | null,
): boolean {
  if (!trendsEnabled || !programActivity) {
    return false;
  }
  if (programActivity.activityType !== ActivityType.Assessment) {
    return false;
  }
  if (!programActivity.completedAt) {
    return false;
  }
  return !isAssessmentAForm(programActivity.programAssessment?.assessment);
}

export function getAssessmentHistorySelection(
  programActivity: ProgramActivityDataFragment,
): AssessmentHistorySelection | undefined {
  const assessment = programActivity.programAssessment?.assessment;
  if (!assessment) {
    return undefined;
  }
  return {
    id: assessment.id,
    type: assessment.type,
    schemaType: assessment.schemaType,
    shortName: assessment.shortName,
    name: assessment.name,
  };
}
